import { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { MagnifyingGlass, WarningCircle, X } from "@phosphor-icons/react";

const sel = "bg-surface-2 border border-border rounded-md text-xs text-fg px-2 py-1.5 focus:outline-none focus:border-accent/50";

function matches(c, f, chosen) {
  const t = f.q.trim().toLowerCase();
  if (t && ![c.contact_name, c.company, c.email, c.role].some((v) => (v || "").toLowerCase().includes(t))) return false;
  if (f.company && c.siren !== f.company) return false;
  if (f.state === "verified" && (!c.verified || c.issue)) return false;
  if (f.state === "unverified" && (c.verified || c.issue)) return false;
  if (f.state === "issue" && !c.issue) return false;
  if (f.show === "selected" && !chosen.has(c.key)) return false;
  if (f.show === "unselected" && chosen.has(c.key)) return false;
  return true;
}

const EmailCell = ({ c, dup }) => (
  <td className="p-2 min-w-[200px]">
    {c.email ? <span className={`font-mono text-[11px] break-words ${c.issue ? "text-muted line-through" : "text-fg"}`}>{c.email}</span> : <span className="text-muted">—</span>}
    <div className="mt-0.5 flex flex-wrap gap-1">
      {c.issue ? <span className="text-[10px] text-amber-500 flex items-center gap-1"><WarningCircle size={11} /> {c.issue}</span>
        : <span className={`text-[10px] ${c.verified ? "text-accent" : "text-muted"}`}>{c.verified ? "vérifiée" : "non vérifiée"}</span>}
      {dup && <span className="text-[10px] text-amber-500" data-testid={`lead-pick-dup-${c.key}`}>doublon · un seul envoi</span>}
    </div>
  </td>
);

export function RecipientPicker({ candidates, selection, onChange, noContacts = [], disabled }) {
  const [f, setF] = useState({ q: "", company: "", state: "", show: "" });
  const chosen = useMemo(() => new Set(selection), [selection]);
  const companies = useMemo(() => [...new Map(candidates.map((c) => [c.siren, c.company])).entries()], [candidates]);
  const dupEmails = useMemo(() => {
    const n = {};
    candidates.forEach((c) => { if (chosen.has(c.key) && !c.issue) n[c.email] = (n[c.email] || 0) + 1; });
    return new Set(Object.keys(n).filter((e) => n[e] > 1));
  }, [candidates, chosen]);
  const rows = candidates.filter((c) => matches(c, f, chosen));
  const selectable = rows.filter((c) => !c.issue);
  const filtersOn = Boolean(f.q || f.company || f.state || f.show);
  const valid = candidates.filter((c) => chosen.has(c.key) && !c.issue);
  const unique = new Set(valid.map((c) => c.email)).size;
  const blocked = candidates.filter((c) => chosen.has(c.key) && c.issue).length;
  const setAll = (on) => {
    const keys = new Set(selectable.map((c) => c.key));
    onChange(on ? [...new Set([...selection, ...keys])] : selection.filter((k) => !keys.has(k)));
  };
  const toggle = (k) => onChange(chosen.has(k) ? selection.filter((x) => x !== k) : [...selection, k]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onePerCompany = () => {
    const done = new Set(candidates.filter((c) => chosen.has(c.key) && !c.issue).map((c) => c.siren));
    const best = new Map();
    selectable.forEach((c) => { if (!done.has(c.siren) && !best.has(c.siren)) best.set(c.siren, c.key); });
    onChange([...selection, ...best.values()]);
  };

  return (
    <div className="space-y-3" data-testid="lead-recipient-picker">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="lead-pick-counter">
        <span className="text-fg"><b className="text-accent text-base" data-testid="lead-pick-count">{unique}</b> destinataire(s) sélectionné(s)</span>
        <span className="text-xs text-muted">sur {candidates.filter((c) => !c.issue).length} contact(s) avec une adresse valide</span>
        {dupEmails.size > 0 && <span className="text-xs text-amber-500">{dupEmails.size} adresse(s) en double : un seul envoi chacune</span>}
        {blocked > 0 && <span className="text-xs text-amber-500">{blocked} contact(s) coché(s) non envoyable(s)</span>}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input value={f.q} onChange={set("q")} placeholder="Rechercher un nom, une entreprise, un email…" className={`${sel} w-full pl-8 py-2`} data-testid="lead-pick-search" />
        </div>
        <select value={f.company} onChange={set("company")} className={`${sel} max-w-[220px]`} data-testid="lead-pick-filter-company">
          <option value="">Toutes les entreprises ({companies.length})</option>
          {companies.map(([s, n]) => <option key={s} value={s}>{n}</option>)}
        </select>
        <select value={f.state} onChange={set("state")} className={sel} data-testid="lead-pick-filter-state">
          <option value="">Toutes les adresses</option><option value="verified">Vérifiées</option>
          <option value="unverified">Non vérifiées</option><option value="issue">Manquantes ou invalides</option>
        </select>
        <select value={f.show} onChange={set("show")} className={sel} data-testid="lead-pick-filter-selection">
          <option value="">Cochés et non cochés</option><option value="selected">Cochés uniquement</option><option value="unselected">Non cochés uniquement</option>
        </select>
        {filtersOn && <button onClick={() => setF({ q: "", company: "", state: "", show: "" })} className="text-xs text-muted hover:text-fg flex items-center gap-1 px-1 py-2" data-testid="lead-pick-reset"><X size={12} /> Effacer les filtres</button>}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button disabled={disabled || !selectable.length} onClick={() => setAll(true)} className="border border-border rounded px-2.5 py-1 hover:border-accent/40 disabled:opacity-40" data-testid="lead-pick-select-all">
          {filtersOn ? `Sélectionner les ${selectable.length} contact(s) filtré(s)` : `Tout sélectionner (${selectable.length})`}
        </button>
        <button disabled={disabled || !rows.some((c) => chosen.has(c.key))} onClick={() => setAll(false)} className="border border-border rounded px-2.5 py-1 hover:border-accent/40 disabled:opacity-40" data-testid="lead-pick-unselect-all">
          {filtersOn ? `Désélectionner les ${rows.filter((c) => chosen.has(c.key) && !c.issue).length} contact(s) filtré(s)` : "Tout désélectionner"}
        </button>
        <button disabled={disabled || !selectable.length} onClick={onePerCompany} className="border border-border rounded px-2.5 py-1 hover:border-accent/40 disabled:opacity-40" data-testid="lead-pick-one-per-company"
          title="Pour chaque entreprise sans contact coché : coche son premier contact valide (adresse vérifiée en priorité)">
          Compléter : 1 contact par entreprise{filtersOn ? " (contacts filtrés)" : ""}
        </button>
        {filtersOn && <span className="text-muted" data-testid="lead-pick-scope-note">Filtres actifs : ces actions ne concernent que les {rows.length} contact(s) affiché(s).</span>}
      </div>
      {!selection.length && candidates.some((c) => !c.issue) && (
        <p className="text-xs text-amber-500" data-testid="lead-pick-empty-selection">Aucun destinataire coché : cochez les contacts qui doivent recevoir cette campagne. Rien n'est présélectionné automatiquement.</p>
      )}
      <div className="border border-border rounded-md overflow-auto max-h-[440px]" data-testid="lead-pick-table">
        <table className="w-full text-xs">
          <thead className="bg-surface-2 text-muted sticky top-0 z-10"><tr>{["", "Contact", "Entreprise", "Adresse email"].map((h, i) => <th key={i} className="text-left p-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.key} className={`border-t border-border/60 ${chosen.has(c.key) ? "bg-accent/[0.04]" : ""}`} data-testid={`lead-pick-row-${c.key}`}>
                <td className="p-2 w-8"><Checkbox checked={chosen.has(c.key)} disabled={disabled || (Boolean(c.issue) && !chosen.has(c.key))} onCheckedChange={() => toggle(c.key)} data-testid={`lead-pick-check-${c.key}`} /></td>
                <td className="p-2 min-w-[140px]"><div className="text-fg">{c.contact_name || <span className="text-muted">Sans nom</span>}</div>
                  <div className="text-[10px] text-muted">{c.role}{c.role ? " · " : ""}{c.first_name ? `{{prenom}} = ${c.first_name}` : <span className="text-amber-500">prénom inconnu</span>}</div></td>
                <td className="p-2 text-muted min-w-[120px]">{c.company}</td>
                <EmailCell c={c} dup={chosen.has(c.key) && dupEmails.has(c.email)} />
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className="p-6 text-center text-muted" data-testid="lead-pick-empty">{candidates.length ? "Aucun contact ne correspond à ces filtres." : "Aucun contact enregistré pour ces entreprises."}</td></tr>}
          </tbody>
        </table>
      </div>
      {noContacts.length > 0 && (
        <p className="text-[11px] text-muted" data-testid="lead-pick-no-contacts">Sans contact enregistré ({noContacts.length}) : {noContacts.map((x) => x.name).join(", ")}. Ajoutez des contacts depuis la fiche prospect.</p>
      )}
    </div>
  );
}
